
import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DesignSystemExamples from '@/docs/DesignSystemExamples';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// Importando a documentação markdown
import designSystemDoc from '@/docs/DesignSystem.md?raw';

/**
 * Componente para renderizar conteúdo markdown
 */
const MarkdownRenderer = ({ content }: { content: string }) => {
  // Na vida real, você usaria uma biblioteca como react-markdown
  // Mas para simplicidade, vamos fazer um parsing básico
  const formatMarkdown = (md: string) => {
    // Substitui cabeçalhos
    let formatted = md
      .replace(/^# (.+)$/gm, '<h1 class="text-3xl font-bold my-4">$1</h1>')
      .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-semibold mt-8 mb-4">$1</h2>')
      .replace(/^### (.+)$/gm, '<h3 class="text-xl font-medium mt-6 mb-3">$1</h3>')
      
      // Substitui listas
      .replace(/^\- (.+)$/gm, '<li class="ml-6 list-disc">$1</li>')
      
      // Trata blocos de código
      .replace(/```(.+?)```/gs, (match, code) => 
        `<pre class="bg-muted p-4 rounded-md my-4 overflow-x-auto"><code>${code.trim()}</code></pre>`
      )
      
      // Substitui links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary underline">$1</a>')
      
      // Substitui parágrafos
      .replace(/^(?!\<[hl])(.+)$/gm, (match, text) => {
        if (text.trim().length === 0) return '';
        if (text.startsWith('<li') || text.startsWith('<pre') || text.startsWith('<table')) return text;
        return `<p class="my-2">${text}</p>`;
      })
      
      // Substitui tabelas
      .replace(/\| (.+) \|/g, '<tr><td>$1</td></tr>')
      .replace(/\| --- \|/g, '');

    return formatted;
  };
  
  return (
    <div 
      className="prose prose-slate dark:prose-invert max-w-none"
      dangerouslySetInnerHTML={{ __html: formatMarkdown(content) }} 
    />
  );
};

/**
 * Página do Sistema de Design
 */
const DesignSystemPage = () => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("documentacao");
  
  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-2xl md:text-3xl">Sistema de Design Vive Calme</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Documentação completa do sistema de design, incluindo temas, animações e utilitários.
          </p>
        </CardContent>
      </Card>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 w-full grid grid-cols-2">
          <TabsTrigger value="documentacao">Documentação</TabsTrigger>
          <TabsTrigger value="exemplos">Exemplos Interativos</TabsTrigger>
        </TabsList>
        
        <TabsContent value="documentacao" className={`${isMobile ? 'mobile-padding' : 'p-6'} bg-card rounded-lg`}>
          <MarkdownRenderer content={designSystemDoc} />
        </TabsContent>
        
        <TabsContent value="exemplos">
          <DesignSystemExamples />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DesignSystemPage;
